import { useLocalStorage } from '@uidotdev/usehooks';
import type { Dispatch, SetStateAction } from 'react';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import useAccount from '~/hooks/useAccount';

import type { AddSiteFormDataTypes } from '../index';

type RequiredAddSiteFields = Required<
  Pick<
    AddSiteFormDataTypes,
    | 'siteName'
    | 'domain'
    | 'path'
    | 'subtype'
    | 'autoGeneration'
    | 'migrationId'
    | 'isMigrating'
  >
>;

type AddSiteFormDataState = AddSiteFormDataTypes &
  RequiredAddSiteFields & {
    siteUrl: string;
  };

const DEFAULT_FORM_DATA: AddSiteFormDataState = {
  siteName: '',
  domain: '',
  path: '',
  subtype: '',
  autoGeneration: false,
  migrationId: '',
  isMigrating: false,
  siteUrl: '',
  title: '',
  action: '',
  admin_email: '',
  admin_user: '',
  datacenter: undefined,
  disk_limit_gib: 10,
  workers: 2,
  clone_site_id: undefined,
  migration_id: '',
  enable_multisite: false,
};

type AddSiteContextValue = {
  formData: AddSiteFormDataState;
  setFormData: Dispatch<SetStateAction<AddSiteFormDataTypes>>;
  clearFormData: () => void;
  chosenSiteId?: number;
  setChosenSiteId: Dispatch<SetStateAction<number | undefined>>;
  clearChosenSiteId: () => void;
  optionSelected?: string;
  setOptionSelected: Dispatch<SetStateAction<string | undefined>>;
  clearOptionSelected: () => void;
  changeLoading: boolean;
  setChangeLoading: Dispatch<SetStateAction<boolean>>;
  clearChangeLoading: () => void;
};

const AddSiteContext = createContext<AddSiteContextValue | undefined>(
  undefined,
);

export const useAddSiteContext = () => {
  const context = useContext(AddSiteContext);

  if (!context) {
    return {
      formData: DEFAULT_FORM_DATA,
      setFormData: () => {},
      clearFormData: () => {},
      chosenSiteId: undefined,
      setChosenSiteId: () => {},
      clearChosenSiteId: () => {},
      optionSelected: undefined,
      setOptionSelected: () => {},
      clearOptionSelected: () => {},
      changeLoading: false,
      setChangeLoading: () => {},
      clearChangeLoading: () => {},
    };
  }

  return context;
};

export const AddSiteProvider = ({ children }: PropsWithChildren) => {
  const { id: hostingId } = useAccount();
  const { pathname } = useLocation();
  const localStorageKey = hostingId ? `/${hostingId}/sites/add` : '/sites/add';

  const [formData, setFormData] = useLocalStorage<AddSiteFormDataTypes>(
    localStorageKey,
    DEFAULT_FORM_DATA,
  );
  const [chosenSiteId, setChosenSiteId] = useState<number | undefined>();
  const [optionSelected, setOptionSelected] = useState<string | undefined>(
    formData?.action || formData?.subtype,
  );
  const [changeLoading, setChangeLoading] = useState<boolean>(false);

  const clearChosenSiteId = useCallback(() => {
    setChosenSiteId(undefined);
  }, []);

  const clearOptionSelected = useCallback(() => {
    setOptionSelected(undefined);
  }, []);

  const clearChangeLoading = useCallback(() => {
    setChangeLoading(false);
  }, []);

  const clearFormData = useCallback(() => {
    localStorage.removeItem(localStorageKey);
  }, [localStorageKey]);

  useEffect(() => {
    if (!pathname.includes('sites/add')) {
      setChangeLoading(false);
    }
  }, [pathname]);

  const mergedFormData = useMemo<AddSiteFormDataState>(
    () => ({
      ...DEFAULT_FORM_DATA,
      ...formData,
    }),
    [formData],
  );

  const value = useMemo(
    () => ({
      formData: mergedFormData,
      setFormData,
      clearFormData,
      chosenSiteId,
      setChosenSiteId,
      clearChosenSiteId,
      optionSelected,
      setOptionSelected,
      clearOptionSelected,
      changeLoading,
      setChangeLoading,
      clearChangeLoading,
    }),
    [
      mergedFormData,
      setFormData,
      clearFormData,
      chosenSiteId,
      optionSelected,
      changeLoading,
      clearChosenSiteId,
      clearOptionSelected,
      clearChangeLoading,
    ],
  );

  return (
    <AddSiteContext.Provider value={value}>{children}</AddSiteContext.Provider>
  );
};
